import React, { useCallback, useId, useMemo, useRef, useState } from "react"
import {
  Button,
  InputNumber,
  Popover,
  Radio,
  Segmented,
  Switch,
  Tooltip,
} from "antd"
import type { RadioChangeEvent } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo, faSliders } from "@fortawesome/free-solid-svg-icons"
import {
  normalizeSignificanceSettings,
  resolveTabSignificance,
  SIGNIFICANCE_PRESETS,
  SignificancePreset,
  SignificanceSettings,
} from "types/significance.types"

export interface SignificanceControlProps {
  /** The settings being edited. `undefined` is "off", or "inherit" when
   *  `allowInherit` is set. */
  value: SignificanceSettings | undefined
  /** Called with the new settings; `undefined` means off (or inherit). */
  onChange: (value: SignificanceSettings | undefined) => void
  /** Adds an "Inherit" choice (an Excel report tab following the report
   *  default). */
  allowInherit?: boolean
  /** The settings an inheriting value resolves to (the report default). */
  inherited?: SignificanceSettings
  /** Which statistics are tested depends on the table type. Omitted for a
   *  setting that spans both (the Excel report default). */
  tableType?: "XTAB" | "DTAB"
  /** Where the helper text goes: under the control (default) or behind an
   *  info icon, for tight toolbars. */
  helperPlacement?: "below" | "tooltip"
}

type Choice = SignificancePreset | "inherit"

export const SIGNIFICANCE_HELPER_TEXT =
  "Letters mark columns this one is significantly higher than. Arrows compare with the rest of the sample."

/** Second levels offered in Advanced (the first level is 95%). */
const SECOND_LEVELS = [90, 80] as const
const DEFAULT_SECOND_LEVEL = 90
const MAX_MIN_BASE = 10000

const TESTED_LABEL: Record<"XTAB" | "DTAB", string> = {
  XTAB: "Col %",
  DTAB: "Percent (yes) and Average",
}

const testedLabel = (tableType: "XTAB" | "DTAB" | undefined): string =>
  tableType
    ? TESTED_LABEL[tableType]
    : `${TESTED_LABEL.XTAB} (XTAB), ${TESTED_LABEL.DTAB} (DTAB)`

const levelsLabel = (levels: readonly number[]): string =>
  levels.map((level) => `${level}%`).join(" + ")

/** "Off", "95%", "95% + 90%" (or the actual levels of custom settings). */
export const describeSignificance = (
  s: SignificanceSettings | undefined
): string => {
  const n = normalizeSignificanceSettings(s)
  return n && n.enabled ? levelsLabel(n.levels) : "Off"
}

/** The segment a value selects: by the number of levels, so custom levels or
 *  advanced tweaks still highlight the matching segment. */
const choiceOf = (
  value: SignificanceSettings | undefined,
  allowInherit: boolean
): Choice => {
  if (allowInherit && value === undefined) return "inherit"
  const n = normalizeSignificanceSettings(value)
  if (!n || !n.enabled) return "off"
  return n.levels.length === 2 ? "95_90" : "95"
}

/**
 * Significance testing in one click: Off / 95% / 95% + 90% (plus Inherit for
 * an Excel report tab), with an Advanced popover for the second level, the
 * vs-rest arrows, the overlap correction and the minimum base.
 *
 * Switching segments changes the levels only: the advanced fields (vs rest,
 * overlap, min base, and the second level when there is one) carry over from
 * the current settings, or from the inherited ones when a tab starts its own
 * override. Advanced is only available while the control's own value is on.
 * Without `allowInherit`, Off emits `undefined` (the stored "off"); with it,
 * Off emits an explicit disabled override and Inherit emits `undefined`.
 */
const SignificanceControl: React.FC<SignificanceControlProps> = ({
  value,
  onChange,
  allowInherit = false,
  inherited,
  tableType,
  helperPlacement = "below",
}) => {
  const name = useId()
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const own = useMemo(() => normalizeSignificanceSettings(value), [value])
  const choice = choiceOf(value, allowInherit)

  // What the advanced fields carry over from when switching segments
  const base = useMemo(
    (): SignificanceSettings =>
      normalizeSignificanceSettings(
        allowInherit ? resolveTabSignificance(value, inherited) : value
      ) ?? SIGNIFICANCE_PRESETS["95"],
    [allowInherit, value, inherited]
  )

  const handleChoice = useCallback(
    (next: Choice) => {
      if (next === "inherit") {
        onChange(undefined)
        return
      }
      if (next === "off") {
        onChange(allowInherit ? { ...base, enabled: false } : undefined)
        return
      }
      const [first] = SIGNIFICANCE_PRESETS[next].levels
      const second =
        base.levels.length === 2 && base.levels[1] < first
          ? base.levels[1]
          : DEFAULT_SECOND_LEVEL
      onChange({
        ...base,
        enabled: true,
        levels: next === "95_90" ? [first, second] : [first],
      })
    },
    [allowInherit, base, onChange]
  )

  const update = useCallback(
    (patch: Partial<SignificanceSettings>) => {
      if (own) onChange({ ...own, ...patch })
    },
    [own, onChange]
  )

  // The minimum base being typed. It is committed on blur, on Enter and when
  // Advanced closes, not on every keystroke: each commit re-runs the tables
  // that use these settings (for a report default, every inheriting tab).
  // `undefined` = not editing; `null` = an empty or invalid entry, which keeps
  // the stored value. The ref holds the latest entry for the commit handlers.
  const [minBaseDraft, setMinBaseDraft] = useState<number | null | undefined>(
    undefined
  )
  const minBaseDraftRef = useRef<number | null | undefined>(undefined)

  const setDraft = (next: number | null | undefined): void => {
    minBaseDraftRef.current = next
    setMinBaseDraft(next)
  }

  const commitMinBase = (): void => {
    const draft = minBaseDraftRef.current
    if (draft === undefined) return
    setDraft(undefined)
    if (typeof draft !== "number" || !own) return
    const next = Math.min(MAX_MIN_BASE, Math.max(0, Math.floor(draft)))
    if (next !== own.minBase) update({ minBase: next })
  }

  const handleAdvancedOpenChange = (open: boolean): void => {
    if (!open) commitMinBase()
    setAdvancedOpen(open)
  }

  const isOn = choice === "95" || choice === "95_90"
  const hasSecondLevel = !!own && own.levels.length === 2

  const options = [
    ...(allowInherit
      ? [
          {
            value: "inherit" as Choice,
            label: `Inherit (${describeSignificance(inherited)})`,
          },
        ]
      : []),
    { value: "off" as Choice, label: "Off" },
    {
      value: "95" as Choice,
      label: choice === "95" && own ? levelsLabel(own.levels) : "95%",
    },
    {
      value: "95_90" as Choice,
      label: choice === "95_90" && own ? levelsLabel(own.levels) : "95% + 90%",
    },
  ]

  const fieldLabel: React.CSSProperties = { fontSize: 12, fontWeight: 500 }
  const fieldHint: React.CSSProperties = {
    fontSize: 11,
    color: "var(--qc-ink-muted, #6b7280)",
  }
  const row: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  }

  const advanced = isOn && own && (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 12, width: 280 }}
      data-testid="significance-advanced"
    >
      <div style={fieldHint}>Tested: {testedLabel(tableType)}</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={fieldLabel}>Second level (lower case letters)</span>
        <Radio.Group
          size="small"
          optionType="button"
          value={hasSecondLevel ? own.levels[1] : undefined}
          disabled={!hasSecondLevel}
          onChange={(e: RadioChangeEvent) =>
            update({ levels: [own.levels[0], Number(e.target.value)] })
          }
          options={SECOND_LEVELS.map((level) => ({
            value: level,
            label: `${level}%`,
          }))}
          aria-label="Second level"
        />
        {!hasSecondLevel && (
          <span style={fieldHint}>Choose 95% + 90% to add a second level.</span>
        )}
      </div>

      <div style={row}>
        <span style={fieldLabel}>Compare with rest of sample</span>
        <Switch
          size="small"
          checked={own.vsRest}
          onChange={(checked) => update({ vsRest: checked })}
          aria-label="Compare with rest of sample"
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={row}>
          <span style={fieldLabel}>Overlap correction</span>
          <Switch
            size="small"
            checked={own.overlap}
            onChange={(checked) => update({ overlap: checked })}
            aria-label="Overlap correction"
          />
        </div>
        <span style={fieldHint}>
          For respondents in more than one column.
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <div style={row}>
          <span style={fieldLabel}>Minimum base</span>
          <InputNumber
            size="small"
            min={0}
            max={MAX_MIN_BASE}
            precision={0}
            value={minBaseDraft === undefined ? own.minBase : minBaseDraft}
            onChange={(v) =>
              setDraft(typeof v === "number" && Number.isFinite(v) ? v : null)
            }
            onBlur={commitMinBase}
            onPressEnter={commitMinBase}
            style={{ width: 96 }}
            aria-label="Minimum base"
          />
        </div>
        <span style={fieldHint}>
          Columns with an unweighted base below this are not tested.
        </span>
      </div>
    </div>
  )

  return (
    <div
      className="dq-significance"
      style={{ display: "flex", flexDirection: "column", gap: 4 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <Segmented<Choice>
          size="small"
          name={name}
          options={options}
          value={choice}
          onChange={handleChoice}
          aria-label="Significance testing"
        />
        <Popover
          trigger="click"
          placement="bottomLeft"
          open={advancedOpen && isOn}
          onOpenChange={handleAdvancedOpenChange}
          title="Advanced significance settings"
          content={advanced}
        >
          <Button
            size="small"
            type="text"
            disabled={!isOn}
            icon={<FontAwesomeIcon icon={faSliders} />}
            aria-label="Advanced significance settings"
          >
            Advanced
          </Button>
        </Popover>
        {helperPlacement === "tooltip" && (
          <Tooltip title={SIGNIFICANCE_HELPER_TEXT} placement="bottom">
            <span
              style={{ color: "var(--qc-ink-muted, #9ca3af)", cursor: "help" }}
              aria-label={SIGNIFICANCE_HELPER_TEXT}
            >
              <FontAwesomeIcon icon={faCircleInfo} />
            </span>
          </Tooltip>
        )}
      </div>
      {helperPlacement === "below" && (
        <div style={fieldHint}>{SIGNIFICANCE_HELPER_TEXT}</div>
      )}
    </div>
  )
}

export default SignificanceControl
